import { Injectable, inject } from '@angular/core';
import { from, map, Observable } from 'rxjs';
import {
  ContractDefinitionCreateRequest,
  ContractDefinitionDetails,
  ContractDefinitionsApi,
  ContractDefinitionUpdateRequest,
  IdResponse
} from '@adesso/dataspace-api-client';
import { ADESSO_API_CONFIG } from '../../../../core/api/adesso-api.config';
import type {
  ContractDefinition,
  ContractDefinitionCreatedInfo,
  ContractDefinitionDeletedInfo,
  ContractDefinitionUpdatedInfo
} from '../models/contract-definition.model';
import { ContractDefinitionApiPort } from '../ports/contract-definition-api.port';

type PropertyBag = Record<string, object>;

@Injectable()
export class AdessoContractDefinitionApiAdapter extends ContractDefinitionApiPort {
  private readonly api = new ContractDefinitionsApi(inject(ADESSO_API_CONFIG));

  getContractDefinitions(): Observable<ContractDefinition[]> {
    return from(this.api.getAllContractDefinitions()).pipe(
      map((res) => (res.data ?? []).map(toDomain))
    );
  }

  createContractDefinition(
    cd: ContractDefinition
  ): Observable<ContractDefinitionCreatedInfo> {
    return from(this.api.createContractDefinition(toCreateRequest(cd))).pipe(
      map((res) => toCreatedInfo(res.data, cd.id))
    );
  }

  updateContractDefinition(
    cd: ContractDefinition
  ): Observable<ContractDefinitionUpdatedInfo> {
    if (!cd.id) {
      throw new Error('ContractDefinitionApiPort.updateContractDefinition requires an id');
    }
    return from(this.api.updateContractDefinition(cd.id, toUpdateRequest(cd))).pipe(
      map(() => ({ updated: true }))
    );
  }

  deleteContractDefinition(id: string): Observable<ContractDefinitionDeletedInfo> {
    return from(this.api.removeContractDefinition(id)).pipe(map(() => ({ deleted: true })));
  }
}

function toDomain(dto: ContractDefinitionDetails): ContractDefinition {
  const pp = (dto.privateProperties ?? {}) as Record<string, unknown>;
  return {
    id: dto.contractDefinitionId ?? '',
    accessPolicyId: dto.accessPolicyId ?? '',
    contractPolicyId: dto.contractPolicyId ?? '',
    assetId: dto.assetSelector ?? '',
    privateProperties: {
      name: str(pp['name']),
      description: str(pp['description'])
    }
  };
}

function toCreateRequest(cd: ContractDefinition): ContractDefinitionCreateRequest {
  return {
    contractDefinitionId: cd.id,
    accessPolicyId: cd.accessPolicyId,
    contractPolicyId: cd.contractPolicyId,
    assetSelector: cd.assetId,
    privateProperties: privatePropertiesToBag(cd.privateProperties)
  };
}

function toUpdateRequest(cd: ContractDefinition): ContractDefinitionUpdateRequest {
  return {
    accessPolicyId: cd.accessPolicyId,
    contractPolicyId: cd.contractPolicyId,
    assetSelector: cd.assetId,
    privateProperties: privatePropertiesToBag(cd.privateProperties)
  };
}

function privatePropertiesToBag(
  pp: ContractDefinition['privateProperties']
): PropertyBag | undefined {
  if (!pp) return undefined;
  const bag: PropertyBag = {};
  if (pp.name) bag['name'] = pp.name as unknown as object;
  if (pp.description) bag['description'] = pp.description as unknown as object;
  return Object.keys(bag).length === 0 ? undefined : bag;
}

function toCreatedInfo(res: IdResponse, fallbackId: string): ContractDefinitionCreatedInfo {
  return {
    id: res.id ?? fallbackId,
    type: 'ContractDefinition',
    createdAt: new Date()
  };
}

function str(v: unknown): string {
  return v == null ? '' : String(v);
}
